import React, { VFC, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { ListItemGroup } from 'components/list-item-group';
import { getModifiersActions } from 'components/product-detail/utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { ModifierGroup } from './product-customization-modifier-group';
import { ProductCustomizationReset } from './product-customization-reset';
import { IModifierGroup, IProductCustomizationModifiersProps } from './types';
import { groupModifiersByDisplayName, isDefaultSelectionAction } from './utils';

export const ProductCustomizationModifiers: VFC<IProductCustomizationModifiersProps> = ({
  modifierSelections,
  modifierDefaults,
  onModifierSelectionsChange,
  onResetModifierSelections,
  pickerAspectExclusions,
  selectedItem,
}) => {
  const { formatMessage } = useIntl();

  const { options: selectedItemModifiers } = selectedItem;

  const enableHideModifierMultipliers = useFlag(LaunchDarklyFlag.ENABLE_HIDE_MODIFIER_MULTIPLIERS);

  const modifierGroupsProps = useMemo<IModifierGroup[]>(() => {
    if (!selectedItemModifiers?.length) {
      return [];
    }

    const modifierGroupsMap = groupModifiersByDisplayName({
      enableHideModifierMultipliers,
      modifiers: selectedItemModifiers,
      modifiersMap: modifierSelections,
      pickerAspectExclusions,
    });
    return Object.entries(modifierGroupsMap).map<IModifierGroup>(
      ([displayGroupName, modifiers]) => ({
        displayGroupName,
        modifiers,
      })
    );
  }, [
    selectedItemModifiers,
    enableHideModifierMultipliers,
    modifierSelections,
    pickerAspectExclusions,
  ]);

  const showResetButton = useMemo(() => {
    return getModifiersActions(modifierSelections).some(
      selAction => !isDefaultSelectionAction(modifierDefaults, selAction)
    );
  }, [modifierSelections, modifierDefaults]);

  return (
    <ListItemGroup
      header={formatMessage({ id: 'customize' })}
      headerAction={
        <ProductCustomizationReset
          showButton={showResetButton}
          onReset={onResetModifierSelections}
        />
      }
      items={modifierGroupsProps.map(modifierGroupProps => (
        <ModifierGroup
          key={modifierGroupProps.displayGroupName}
          {...modifierGroupProps}
          onModifierSelectionChange={onModifierSelectionsChange}
          selectedItem={selectedItem}
        />
      ))}
    />
  );
};
